/* ohm-gclid-capture.js — carry the Google Ads click ID from landing page to order.

   WHY THIS EXISTS

   The 2026-08-24 ads audit found the account's only revenue signal was a GA4
   purchase import: 6.9 conversions worth $6,201 over twelve months, against
   $113,694 of bike revenue in the last sixty days alone. Every bid decision was
   being made on a modelled, lossy fraction of what the store actually sells.

   Auto-tagging is enabled, so every paid click already arrives with `?gclid=`.
   Nothing was reading it. Shopify normalises `landingPage` without its query
   string — verified: 0 of 92 recorded journey moments retained any query
   string — so the click ID was being dropped on arrival and could never be
   joined back to an order.

   This stores the click ID and writes it onto the cart, so it lands on the order
   as a note attribute. `scripts/ads-upload-offline-conversions.mjs` then uploads
   each order to Google Ads with its REAL total, bikes included.

   DESIGN NOTES

   - Under-counts rather than over-counts. A click ID only survives if the buyer
     returns in the same browser, so some real conversions are missed. That is
     the opposite failure mode to GA4's modelling, and the safer one to have.
   - gbraid/wbraid are captured too. They replace gclid on iOS app/web traffic
     where gclid is withheld, and the upload API accepts them in its place.
   - 90-day expiry matches the conversion action's click lookback window. An ID
     older than the window is useless to the upload and is discarded here rather
     than being written onto an order that can never be matched.
   - Writes to the cart at most once per session per ID. `/cart/update.js`
     accepts attributes on an empty cart, so capture does not wait for an
     add-to-cart that may never come.
   - Purely additive. It never blocks navigation, never touches checkout, and a
     failed write leaves the order intact and simply unattributed.
*/

(function () {
  'use strict';

  var PARAMS = ['gclid', 'gbraid', 'wbraid'];
  var STORE_KEY = 'ohm_gads_click';
  var SESSION_FLAG = 'ohm_gads_click_written';
  var MAX_AGE_DAYS = 90;           // must not exceed the conversion action's click lookback
  var ATTR_ID = 'ohm_gads_click_id';
  var ATTR_TYPE = 'ohm_gads_click_type';
  var ATTR_AT = 'ohm_gads_click_at';

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || !v.id || !v.at) return null;
      var ageDays = (Date.now() - v.at) / 86400000;
      if (ageDays > MAX_AGE_DAYS) { window.localStorage.removeItem(STORE_KEY); return null; }
      return v;
    } catch (e) { return null; }   // private mode / storage disabled
  }

  function capture() {
    var qs;
    try { qs = new URLSearchParams(window.location.search); } catch (e) { return null; }
    for (var i = 0; i < PARAMS.length; i++) {
      var val = qs.get(PARAMS[i]);
      if (!val) continue;
      var rec = { id: val, type: PARAMS[i], at: Date.now() };
      try { window.localStorage.setItem(STORE_KEY, JSON.stringify(rec)); } catch (e) { /* still usable this page */ }
      try { window.sessionStorage.removeItem(SESSION_FLAG); } catch (e) {}   // new click => rewrite
      return rec;
    }
    return null;
  }

  function alreadyWritten(rec) {
    try { return window.sessionStorage.getItem(SESSION_FLAG) === rec.id; } catch (e) { return false; }
  }

  function markWritten(rec) {
    try { window.sessionStorage.setItem(SESSION_FLAG, rec.id); } catch (e) {}
  }

  function writeToCart(rec) {
    if (alreadyWritten(rec)) return;
    var attrs = {};
    attrs[ATTR_ID] = rec.id;
    attrs[ATTR_TYPE] = rec.type;
    attrs[ATTR_AT] = new Date(rec.at).toISOString();
    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
            ? window.Shopify.routes.root + 'cart/update.js'
            : '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) {
      if (r && r.ok) markWritten(rec);
    }).catch(function () { /* unattributed order is an acceptable outcome */ });
  }

  var rec = capture() || readStored();
  if (rec) writeToCart(rec);
})();
